// 小物札(114) の表示行生成とラベルキャッシュ補助をまとめる service です。
export function createKomonoFudaDisplayService(deps) {
    const {
        entrydataRef,
        entrydetailRef,
        selectedFukushizaiContextRef,
        pendingFukushizaiCopy,
        komonoFudaLabelCacheRef,
        komonoFudaLabelLoadingRef,
        KOMONOFUDA_SIZE_RATE_FIELD_IDS,
        gridlist,
        fnc,
        corpkey,
        account,
        komonoFudaLabelsUrl,
        isEmpty,
        isEnabled,
        DicVal,
        normalizeFukushizaiContext,
        setFukushizaiDisplayMeta,
        extractMasterCodeFromDisplayValue,
        readFukushizaiBaseMasterName,
        isKomonofudaPanelInfo,
        debugFukushizaiSyncLog,
        fukushizaiDetailSyncingRef,
        applyFukushizaiSectionPanelRows,
        GridData,
        SetGridData,
        GridApi,
        entryLoadingRef,
        isGridDomReady,
        applyFukushizaiDisplayRowsNow
    } = deps;

    // 小物札表示でのみ使う UI 専用列かを判定します。
    function isKomonofudaUiOnlyField(key) {
        const cid = String(key ?? "");
        return cid === "SSDZ_KOMONOFUDACD"
            || cid === "KOMONOFUDA_NAIYO_DMY"
            || cid === "__fukushizaiBaseRowId"
            || cid === "__fukushizaiSourceColumnId"
            || cid === "__komonofudaBaseRowId"
            || cid === "__komonofudaIndex"
            || cid === "__komonofudaSourceColumnId"
            || cid === "__komonofudaLabel"
            || cid === "__komonofudaCode"
            || cid === "__komonofudaName"
            || cid === "__fukushizaiCopyPreview";
    }

    // API 応答のラベル配列を画面表示向けに正規化します。
    function normalizeKomonoFudaLabelRows(labels) {
        const list = Array.isArray(labels) ? labels : [];
        return list
            .map((row, index) => {
                const label = String(DicVal(row, "name1", DicVal(row, "name", DicVal(row, "label", ""))) ?? "").trim();
                return {
                    index: Number(DicVal(row, "index", index + 1) || index + 1),
                    code: String(DicVal(row, "code", "") ?? "").trim(),
                    name1: label,
                    sort: Number(DicVal(row, "sort", index + 1) || index + 1)
                };
            })
            .filter((row) => row.name1 !== "")
            .sort((a, b) => {
                if (a.index !== b.index) return a.index - b.index;
                return a.sort - b.sort;
            });
    }

    // 小物札ラベル API 応答を共通形式へそろえます。
    function normalizeKomonoFudaLabelResponse(data, fallbackCode = "", fallbackName = "") {
        const source = isEmpty(data) ? {} : data;
        const rows = normalizeKomonoFudaLabelRows(DicVal(source, "labels", DicVal(source, "rows", DicVal(source, "rowdata", []))));
        const fuzokuName = String(DicVal(source, "fuzokuName", fallbackName) ?? "").trim();
        return {
            status: DicVal(source, "status", true) !== false,
            fuzokuHinban: String(DicVal(source, "fuzokuHinban", fallbackCode) ?? "").trim(),
            fuzokuName,
            komonoKbn: String(DicVal(source, "komonoKbn", "") ?? "").trim(),
            source: String(DicVal(source, "source", rows.length === 0 ? "" : "response") ?? "").trim(),
            sourceMissing: isEnabled(DicVal(source, "sourceMissing", rows.length === 0)),
            labels: rows
        };
    }

    // ブランドコード込みのキャッシュキー生成に使うブランドコードを返します。
    function readKomonoFudaBrandCode() {
        return String(DicVal(entrydataRef.current, "PDT_BRDCODE", DicVal(entrydataRef.current, "BRD_CODE", "")) ?? "").trim();
    }

    // 小物札ラベルキャッシュのキーを生成します。
    function buildKomonoFudaLabelCacheKey(code, brandCode = readKomonoFudaBrandCode()) {
        const fuzokuHinban = String(code ?? "").trim();
        const brand = String(brandCode ?? "").trim();
        return brand === "" ? fuzokuHinban : `${fuzokuHinban}|${brand}`;
    }

    // キャッシュ未取得時も、最低限の fallback 表示情報を返します。
    function getKomonoFudaLabelCache(code, fallbackName = "") {
        const fuzokuHinban = String(code ?? "").trim();
        if (fuzokuHinban === "") {
            return normalizeKomonoFudaLabelResponse({ labels: [] }, "", fallbackName);
        }
        const key = buildKomonoFudaLabelCacheKey(fuzokuHinban);
        const cached = DicVal(komonoFudaLabelCacheRef.current, key, null);
        if (!isEmpty(cached)) return cached;
        const fallbackLabels = String(fallbackName ?? "").trim() === ""
            ? []
            : [{ index: 1, code: "", name1: String(fallbackName ?? "").trim(), sort: 1 }];
        return normalizeKomonoFudaLabelResponse({
            status: true,
            fuzokuHinban,
            fuzokuName: fallbackName,
            source: fallbackLabels.length === 0 ? "" : "MASTER_NAME_FALLBACK",
            sourceMissing: true,
            labels: fallbackLabels
        }, fuzokuHinban, fallbackName);
    }

    // 現在表示中の小物札コードに対する応答かを判定します。
    function isCurrentKomonoFudaLabelRequest(code, brandCode) {
        const currentCode = extractMasterCodeFromDisplayValue(DicVal(entrydataRef.current, "SSDZ_KOMONOFUDACD", ""));
        return currentCode === String(code ?? "").trim()
            && readKomonoFudaBrandCode() === String(brandCode ?? "").trim();
    }

    // 小物札コードを持つ行からラベル取得を開始します。
    function requestKomonoFudaLabelsForRow(row) {
        if (isEmpty(row)) return;
        const code = extractMasterCodeFromDisplayValue(DicVal(row, "SSDZ_KOMONOFUDACD", ""));
        if (code === "") return;
        requestKomonoFudaLabels(code, readFukushizaiBaseMasterName(row, "SSDZ_KOMONOFUDACD"));
    }

    // 小物札ラベルを取得してキャッシュし、必要なら表示パネルを再描画します。
    function requestKomonoFudaLabels(code, fallbackName = "") {
        const fuzokuHinban = String(code ?? "").trim();
        if (fuzokuHinban === "") return;
        const brandCode = readKomonoFudaBrandCode();
        const cacheKey = buildKomonoFudaLabelCacheKey(fuzokuHinban, brandCode);
        if (!isEmpty(DicVal(komonoFudaLabelCacheRef.current, cacheKey, null))) return;
        if (isEnabled(DicVal(komonoFudaLabelLoadingRef.current, cacheKey, false))) return;
        if (isEmpty(fnc.current) || typeof fnc.current.postaxios !== "function") return;

        komonoFudaLabelLoadingRef.current[cacheKey] = true;
        const param = {
            corpkey,
            account,
            fuzokuHinban,
            brandCode
        };
        const finish = function () {
            // 本要求は nowait(第4引数 true)でローディングを出していないため、ここでは解除しない。
            // 解除すると並行して動いている別処理のローディングまで消してしまう。
            komonoFudaLabelLoadingRef.current[cacheKey] = false;
        };

        try {
            fnc.current.postaxios(komonoFudaLabelsUrl, param, {
                onSuccess: function (data) {
                    try {
                        const next = normalizeKomonoFudaLabelResponse(data, fuzokuHinban, fallbackName);
                        komonoFudaLabelCacheRef.current[cacheKey] = next;
                        debugFukushizaiSyncLog("[Kakousiji][komonofuda labels loaded]", {
                            fuzokuHinban,
                            brandCode,
                            source: next.source,
                            sourceMissing: next.sourceMissing,
                            labelCount: next.labels.length
                        });
                        if (isCurrentKomonoFudaLabelRequest(fuzokuHinban, brandCode)) {
                            refreshKomonoFudaDisplayPanelOnly();
                        }
                    } finally {
                        finish();
                    }
                },
                onError: function (data) {
                    try {
                        komonoFudaLabelCacheRef.current[cacheKey] = normalizeKomonoFudaLabelResponse({
                            status: false,
                            fuzokuHinban,
                            fuzokuName: fallbackName,
                            source: fallbackName === "" ? "" : "MASTER_NAME_FALLBACK",
                            sourceMissing: true,
                            labels: fallbackName === "" ? [] : [{ index: 1, code: "", name1: fallbackName, sort: 1 }]
                        }, fuzokuHinban, fallbackName);
                        debugFukushizaiSyncLog("[Kakousiji][komonofuda labels failed]", {
                            fuzokuHinban,
                            brandCode,
                            errDescription: DicVal(data, "errDescription", "")
                        });
                        if (isCurrentKomonoFudaLabelRequest(fuzokuHinban, brandCode)) {
                            refreshKomonoFudaDisplayPanelOnly();
                        }
                    } finally {
                        finish();
                    }
                }
            }, true);
        } catch (error) {
            finish();
            debugFukushizaiSyncLog("[Kakousiji][komonofuda labels exception]", {
                fuzokuHinban,
                brandCode,
                error
            });
        }
    }

    // 小物札パネル定義を取得します。
    function findKomonoFudaPanelInfo() {
        if (!Array.isArray(gridlist) || gridlist.length === 0) return null;
        return gridlist.find((panelInfo) => isKomonofudaPanelInfo(panelInfo)) || null;
    }

    // 小物札パネルだけを再描画します。
    function refreshKomonoFudaDisplayPanelOnly(previewOverride = pendingFukushizaiCopy) {
        const panelInfo = findKomonoFudaPanelInfo();
        if (isEmpty(panelInfo)) return;
        const previousSyncing = fukushizaiDetailSyncingRef.current;
        fukushizaiDetailSyncingRef.current = true;
        try {
            applyFukushizaiSectionPanelRows(panelInfo, entrydetailRef.current, previewOverride, selectedFukushizaiContextRef.current);
        } finally {
            fukushizaiDetailSyncingRef.current = previousSyncing;
        }
    }

    // 小物札表示グリッドの表示内容だけをクリアします。
    function clearKomonoFudaDisplayGridContents() {
        const panelInfo = findKomonoFudaPanelInfo();
        if (isEmpty(panelInfo)) return;
        const rows = GridData(panelInfo);
        if (!Array.isArray(rows) || rows.length === 0) return;
        const nextRows = rows.map((row) => {
            if (isEmpty(row) || isEnabled(DicVal(row, "__fukushizaiCopyPreview", false))) return row;
            const next = { ...row, KOMONOFUDA_NAIYO_DMY: "" };
            KOMONOFUDA_SIZE_RATE_FIELD_IDS.forEach((fieldId) => {
                next[fieldId] = "";
            });
            delete next.__komonofudaName;
            return next;
        });
        SetGridData(panelInfo, nextRows);
        if (!entryLoadingRef.current && !isEmpty(GridApi(panelInfo)) && isGridDomReady(panelInfo)) {
            applyFukushizaiDisplayRowsNow(panelInfo, nextRows);
        }
    }

    // detail 1 行を、小物札表示用の複数行へ展開します。
    function buildKomonoFudaDisplayRows(rows, contextOverride = null) {
        const list = Array.isArray(rows) ? rows : [];
        const buildContext = normalizeFukushizaiContext(contextOverride || selectedFukushizaiContextRef.current);
        const baseRow = list.find((row) => !isEmpty(row)) || {};
        const baseRowId = String(DicVal(baseRow, "rowid", "0") ?? "0").trim();
        const selectedCode = String(DicVal(baseRow, "__komonofudaCode", "") ?? "").trim()
            || extractMasterCodeFromDisplayValue(DicVal(baseRow, "SSDZ_KOMONOFUDACD", ""));
        const selectedName = readFukushizaiBaseMasterName(baseRow, "SSDZ_KOMONOFUDACD");
        const labelInfo = getKomonoFudaLabelCache(selectedCode, selectedName);
        const labels = normalizeKomonoFudaLabelRows(DicVal(labelInfo, "labels", []));
        const contentCount = KOMONOFUDA_SIZE_RATE_FIELD_IDS.reduce((max, sourceColumnId, index) => {
            const value = String(DicVal(baseRow, sourceColumnId, "") ?? "").trim();
            return value !== "" ? index + 1 : max;
        }, 0);
        const rowCount = Math.min(KOMONOFUDA_SIZE_RATE_FIELD_IDS.length, Math.max(labels.length, contentCount));
        if (rowCount <= 0) return [];

        return Array.from({ length: rowCount }, (_, index) => {
            const sourceColumnId = KOMONOFUDA_SIZE_RATE_FIELD_IDS[index] || "";
            const slot = index + 1;
            const labelRow = labels.find((row) => Number(DicVal(row, "index", 0)) === slot) || labels[index] || {};
            const labelName1 = String(DicVal(labelRow, "name1", "") ?? "").trim();
            const nextRow = { ...baseRow };
            nextRow.rowid = String(index);
            setFukushizaiDisplayMeta(nextRow, baseRowId, sourceColumnId);
            nextRow.__komonofudaBaseRowId = baseRowId;
            nextRow.__komonofudaIndex = slot;
            nextRow.__komonofudaSourceColumnId = sourceColumnId;
            nextRow.__komonofudaLabel = labelName1;
            nextRow.__komonofudaCode = selectedCode;
            nextRow.__komonofudaName = String(DicVal(labelInfo, "fuzokuName", selectedName) ?? "").trim();
            nextRow.SSDZ_KOMONOFUDACD = labelName1;
            nextRow.KOMONOFUDA_NAIYO_DMY = String(DicVal(baseRow, sourceColumnId, "") ?? "");
            return nextRow;
        });
    }

    return {
        isKomonofudaUiOnlyField,
        normalizeKomonoFudaLabelRows,
        normalizeKomonoFudaLabelResponse,
        readKomonoFudaBrandCode,
        buildKomonoFudaLabelCacheKey,
        getKomonoFudaLabelCache,
        isCurrentKomonoFudaLabelRequest,
        requestKomonoFudaLabelsForRow,
        requestKomonoFudaLabels,
        findKomonoFudaPanelInfo,
        refreshKomonoFudaDisplayPanelOnly,
        clearKomonoFudaDisplayGridContents,
        buildKomonoFudaDisplayRows
    };
}
